// @flow
import React, {Component} from 'react';
import AppsUtil from '../../../../utils/AppsUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import './GetHelp.scss';
import Popover from '@react/react-spectrum/Popover';
import Button from '@react/react-spectrum/Button';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import {AUTO_DOWNLOAD_QUERY_PARAM, AUTO_DOWNLOAD_TIMER} from '../../../../constants/AppPicker/appPickerConstants';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';

const ANALYTICS = {
	RENDER: 'render',
	CANCEL: 'cancel',
	START: 'start',
	AUTO: 'auto'
};

type Props = {
	environment: string,
	history: Object,
	hide: Function
};

type State = {
	time: number
};

export default class AutoDownload extends Component<Props, State> {
	countDownInterval: IntervalID;
	state: State = {
		time: AUTO_DOWNLOAD_TIMER
	};

	constructor(props: Props) {
		super(props);
		HelpPopoverUtil.closeAutoDownloadPopover = this.props.hide.bind(this);
	}

	get defaultProductConfig() {
		const sapCode = AppPickerUtil.getDefaultDownloadProduct();
		const ffcData = AppsUtil.getFFCProductConfigBySapCode(sapCode);
		return {
			sapCode,
			appName: ffcData && ffcData.displayName,
			platform: AppsUtil.getProductPlatform(ffcData)
		};
	}

	get popoverDescription() {
		if (AppPickerUtil.getDefaultDownloadProduct() === 'KCCC' && AppsUtil.hasThor) {
			return 'apps.allAppsAppPicker.autoDownoad.description.thorOpen';
		}
		return 'apps.allAppsAppPicker.autoDownoad.description';
	}

	componentDidMount() {
		this.handleAnalyticsEvent(ANALYTICS.RENDER);
		this.countDownInterval = setInterval(() => {
			const remTime = this.state.time;
			if (remTime) {
				this.setState({time: remTime - 1});
			}
			else {
				clearInterval(this.countDownInterval);
				this.handleAnalyticsEvent(ANALYTICS.AUTO);
				this.startDownload(false);
			}
		}, 1000);
	}

	componentWillUnmount() {
		clearInterval(this.countDownInterval);
	}

	handleAnalyticsEvent(eventType: string) {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		const event: Object = {
			subcategory: 'Inactivity'
		};
		const sapCode = this.defaultProductConfig.sapCode || '';
		const supportedAppVersion = CompatibilityUtil.getSupportedAppVersion(sapCode);
		const latestAppVersion = AppsUtil.getFFCProductConfigBySapCode(sapCode).version;
		const content = {
			id: sapCode,
			name: this.defaultProductConfig.appName,
			type: analytics.getCompatibilityString(
				CompatibilityUtil.isAppSupported(sapCode),
				CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
			),
			version: supportedAppVersion
		};
		const olderVersion = (supportedAppVersion !== latestAppVersion) ? 'Older Version' : undefined;
		const ui = {
			view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
		};
		switch (eventType) {
			case ANALYTICS.RENDER:
				event.type = 'render';
				event.subtype = 'download-modal';
				event.value = '75 seconds';
				break;
			case ANALYTICS.CANCEL:
				event.type = 'click';
				event.subtype = 'cancel';
				event.value = olderVersion;
				break;
			case ANALYTICS.START:
				event.type = 'click';
				event.subtype = 'download';
				event.value = olderVersion;
				break;
			case ANALYTICS.AUTO:
				event.type = 'auto';
				event.subtype = 'download';
				event.value = '85 seconds';
				break;
		}
		analytics.genericAnalyticEvent({
			event,
			content,
			ui
		});
	}

	startDownload(onStartClick: boolean = false) {
		const url = `${AppsUtil.getInstallationModalUrl({
			sapCode: this.defaultProductConfig.sapCode,
			source: AppPickerUtil.getSourceQueryParam(),
			platform: this.defaultProductConfig.platform
		})}&isStarter=true&${AUTO_DOWNLOAD_QUERY_PARAM.QUERY_PARAM}=${onStartClick ? AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO_WITH_ACTION : AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO}`;
		if (AppsUtil.hasFeature('temp_cch_10855')) {
			window._uxa = window._uxa || [];
			window._uxa.push(['trackPageview', url]);
		}
		this.props.history.push(url);
		this.props.hide();
	}

	onStartClick() {
		this.handleAnalyticsEvent(ANALYTICS.START);
		this.startDownload(true);
	}

	onCancelClick() {
		this.handleAnalyticsEvent(ANALYTICS.CANCEL);
		this.props.hide();
	}

	render() {
		const {history, hide, ...restProps} = this.props; // eslint-disable-line no-unused-vars
		const {appName} = this.defaultProductConfig;
		const {time} = this.state;
		return (
			<Popover {...restProps} className='auto-download-popover'>
				<div className='autoDownload-container'>
					<div className='autoDownload-heading'>
						{AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.autoDownoad.heading'}, {appName})}
					</div>
					<div className='autoDownload-description'>
						{AppsUtil.intl.formatMessage({id: this.popoverDescription}, {appName, time})}
					</div>
					<div className='autoDownload-actions'>
						<Button variant='primary' quiet label={AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.autoDownoad.cancel'})} onClick={() => {this.onCancelClick();}}/>
						<Button variant='primary' label={AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.autoDownoad.start'})} onClick={() => {this.onStartClick();}}/>
					</div>
				</div>
			</Popover>
		);
	}
}
